import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { AGENTS } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import { Plus, Bot, MessageSquare } from "lucide-react";

export default function AgentsList() {
  const [agents, setAgents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [creatingFor, setCreatingFor] = useState(null);

  const load = async () => {
    try {
      const [a, l] = await Promise.all([
        api.get("/agents"),
        api.get("/leads"),
      ]);
      setAgents(a.data.agents);
      setLeads(l.data.leads);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const leadsWithoutAgent = leads.filter(
    (l) => !agents.some((a) => a.lead_id === l.id)
  );

  return (
    <div data-testid={AGENTS.listContainer} className="p-10">
      <Toaster position="top-right" richColors />
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="overline mb-3">AI product</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">
            AI Agents
          </h1>
          <p className="text-neutral-500 mt-2 max-w-xl">
            One AI assistant per lead — handles FAQ, customer service, and books
            appointments. Auto-embedded on the free preview website.
          </p>
        </div>
      </div>

      {/* Existing agents */}
      <div className="bg-white border border-neutral-200 mb-8">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <div className="overline mb-1">Deployed</div>
            <h2 className="font-display text-xl font-semibold">
              {agents.length} active agents
            </h2>
          </div>
        </div>
        {agents.length === 0 ? (
          <div className="p-12 text-center text-neutral-500">
            <Bot className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
            No agents yet. Create your first one from a lead below.
          </div>
        ) : (
          <table className="lf-grid">
            <thead>
              <tr>
                <th>Agent</th>
                <th>For business</th>
                <th>Role</th>
                <th>Booking</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.id} data-testid={AGENTS.row(a.id)}>
                  <td>
                    <div className="font-semibold">{a.name}</div>
                    <div className="text-xs text-neutral-500">{a.tagline}</div>
                  </td>
                  <td>
                    <div className="font-semibold">{a.lead_name}</div>
                    <div className="text-xs text-neutral-500">
                      {a.lead_industry} · {a.lead_city}
                    </div>
                  </td>
                  <td>
                    <span className="pill pill-new">{a.role}</span>
                  </td>
                  <td>{a.booking_enabled ? "Enabled" : "Off"}</td>
                  <td className="text-right">
                    <Link
                      to={`/agents/${a.id}`}
                      className="lf-btn-outline text-xs px-3 py-1.5"
                    >
                      Edit & test
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create new for existing leads */}
      <div className="bg-white border border-neutral-200">
        <div className="px-6 py-4 border-b border-neutral-200">
          <div className="overline mb-1">Add coverage</div>
          <h2 className="font-display text-xl font-semibold">
            Leads without an agent ({leadsWithoutAgent.length})
          </h2>
        </div>
        {leadsWithoutAgent.length === 0 ? (
          <div className="p-8 text-neutral-500 text-sm">
            Every lead has an agent. 
          </div>
        ) : (
          <table className="lf-grid">
            <tbody>
              {leadsWithoutAgent.map((l) => (
                <tr key={l.id}>
                  <td>
                    <div className="font-semibold">{l.name}</div>
                    <div className="text-xs text-neutral-500">
                      {l.industry} · {l.city}
                    </div>
                  </td>
                  <td className="text-right">
                    <Link
                      to={`/agents/new?lead_id=${l.id}`}
                      data-testid={AGENTS.newBtn}
                      className="lf-btn-primary inline-flex items-center gap-2 text-xs"
                    >
                      <Plus className="w-3.5 h-3.5" /> Create agent
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
